import React, { forwardRef, useEffect } from "react";

import { useSplunkEvents } from "../hooks/useSplunkEvents";
import { AutoPlayStatus } from "../../globals";
import { useBlockHomepageRefresh } from "../hooks";
import { Subtitles } from "../../controls/Subtitles";
import { getIsVertical } from "../utils";
import { useVideoHandlers } from "../hooks/useVideoHandlers";
import { useVideoDataContext } from "../contexts/VideoDataContext";
import { useAutoplayContext } from "../contexts/AutoplayContext";
import { useStickyVideoContext } from "../contexts/StickyVideoContext";
import { useHlsContext } from "../contexts/HlsContext";
import { useOrcaLogs } from "../hooks/useOrcaLogs";
import PropTypes from "prop-types";
import { useOrcaCommonContext } from "../contexts/OrcaCommonContext";
import { useOrcaRender } from "../hooks/useOrcaRender";
import { OrcaVideo } from "./OrcaVideo";

export const OrcaCommonUI = forwardRef(
  (
    {
      onVideoRendered = () => {},
      config = {},
      promoEnabled = true,
      handleVideoEnd = () => {},
      isLive = false,
      children = null,
      handleStart = () => {},
      transitionToVideo = () => {},
      updateCues = () => {},
      hasSubtitles = false,
      currentCue = null,
      updateDisplay = () => {},
      carouselState = null,
      Controls,
      handleClosePlayer = () => {}
    },
    ref
  ) => {
    const { isCarousel = false, debug, hideControls = false } = config;

    const { videoData, videoId } = useVideoDataContext();
    const { autoplayState, updateAutoplayState } = useAutoplayContext();
    const { isStickyEnabled, updateStickyContext } = useStickyVideoContext();
    const { setUpVideoSource, hls } = useHlsContext();
    const { initializeNewVideo, renderControls, setRenderControls } =
      useOrcaCommonContext();

    const isVertical = getIsVertical(videoData);

    useOrcaLogs(videoData, videoId);

    useBlockHomepageRefresh();

    // Setup Splunk events
    const splunkParams = { isLive };
    useSplunkEvents(splunkParams, config);

    // Functions surfaced on the orcaplayer ref
    useVideoHandlers(
      ref,
      hls,
      setRenderControls,
      promoEnabled,
      initializeNewVideo,
      setUpVideoSource,
      transitionToVideo
    );

    // Kickoff autoplay flow
    useEffect(() => {
      if (autoplayState.status !== AutoPlayStatus.INIT) return;
      handleStart();
      updateAutoplayState({
        status: AutoPlayStatus.READY
      });
      // We only want this to run directly after initializeNewVideo sets autoplayState.status
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoplayState.status]);

    useOrcaRender(ref, config, isLive, onVideoRendered);

    return (
      <>
        <OrcaVideo
          updateCues={updateCues}
          handleVideoEnd={handleVideoEnd}
          isStickyEnabled={isStickyEnabled}
          updateStickyContext={updateStickyContext}
          updateDisplay={updateDisplay}
          debug={debug}
          preload={config.preload}
        />
        {children}
        {renderControls && !hideControls && (
          <>
            {hasSubtitles && currentCue && (
              <Subtitles currentCue={currentCue} />
            )}
            <Controls
              config={config}
              hasSubtitles={hasSubtitles}
              isCarousel={isCarousel}
              carouselEnd={carouselState?.isEnd}
              closeCarousel={handleClosePlayer}
              isLive={isLive}
              debug={debug}
              isVertical={isVertical}
            />
          </>
        )}
      </>
    );
  }
);

OrcaCommonUI.displayName = "OrcaCommonUI";

OrcaCommonUI.propTypes = {
  onVideoRendered: PropTypes.func,
  config: PropTypes.object,
  promoEnabled: PropTypes.bool,
  liveEventEnded: PropTypes.bool,
  handleVideoEnd: PropTypes.func,
  isLive: PropTypes.bool,
  children: PropTypes.node,
  handleStart: PropTypes.func,
  transitionToVideo: PropTypes.func,
  updateCues: PropTypes.func,
  hasSubtitles: PropTypes.bool,
  currentCue: PropTypes.object,
  updateDisplay: PropTypes.func,
  Controls: PropTypes.elementType,
  carouselState: PropTypes.object,
  handleClosePlayer: PropTypes.func
};
